// src/pages/Auth/AuthCallback.jsx
// ✅ FIXED:
// - Saves token + user to localStorage BEFORE calling refreshUser
// - navigate() uses replace:true so back button doesn't return to callback
// - Handles needs_role flag for new Google users

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Box, CircularProgress, Typography, Alert, Button, Paper,
} from '@mui/material';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const AuthCallback = () => {
  const [status, setStatus] = useState('loading');
  const [error,  setError]  = useState('');
  const hasRun = useRef(false);

  const navigate        = useNavigate();
  const location        = useLocation();
  const { refreshUser } = useAuth();

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    processCallback();
  }, []);

  const processCallback = async () => {
    try {
      const params = new URLSearchParams(location.search);
      const key    = params.get('key');

      if (!key) {
        throw new Error('Authentication key missing. Please try logging in again.');
      }

      console.log('🔄 AuthCallback: exchanging key...');

      const response = await authAPI.googleExchange({ key });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Authentication failed');
      }

      const { user, access_token, needs_role } = response.data;

      if (!access_token) {
        throw new Error('No access token received from server');
      }

      // ✅ Save FIRST — before refreshUser so context reads the right token
      localStorage.setItem('token', access_token);
      localStorage.setItem('user',  JSON.stringify(user));

      console.log('✅ Token saved for:', user?.email);

      // Update AuthContext so header/nav re-renders
      if (typeof refreshUser === 'function') {
        await refreshUser();
      }

      setStatus('success');
      toast.success(`Welcome, ${user?.company_name || user?.email || 'there'}!`);

      // Navigate to correct destination
      setTimeout(() => {
        if (needs_role || !user?.user_type) {
          navigate('/auth/select-role', { replace: true });
        } else if (user.user_type === 'admin') {
          navigate('/admin/dashboard',  { replace: true });
        } else if (user.user_type === 'seller') {
          navigate('/seller/dashboard', { replace: true });
        } else {
          navigate('/buyer/dashboard',  { replace: true });
        }
      }, 1200);

    } catch (err) {
      console.error('AuthCallback error:', err);
      const msg = err.response?.data?.message || err.message || 'Authentication failed';
      setError(msg);
      setStatus('error');
      toast.error(msg);
    }
  };

  if (status === 'loading') {
    return (
      <Container maxWidth="sm">
        <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center',
                   justifyContent:'center', minHeight:'100vh', textAlign:'center' }}>
          <Paper sx={{ p:4, width:'100%', borderRadius:3, boxShadow:3 }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt:3, fontWeight:600 }}>
              Processing authentication...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt:1 }}>
              Please wait while we complete your login
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (status === 'success') {
    return (
      <Container maxWidth="sm">
        <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center',
                   justifyContent:'center', minHeight:'100vh', textAlign:'center' }}>
          <Paper sx={{ p:4, width:'100%', borderRadius:3, boxShadow:3 }}>
            <Box sx={{ color:'success.main', mb:3 }}>
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="transparent"/>
                <path d="M7 12L10 15L17 8" stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Box>
            <Typography variant="h5" sx={{ mb:2, fontWeight:600 }}>Login Successful! 🎉</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb:3 }}>
              Redirecting you to your dashboard...
            </Typography>
            <CircularProgress size={24} />
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center',
                 justifyContent:'center', minHeight:'100vh', textAlign:'center' }}>
        <Paper sx={{ p:4, width:'100%', borderRadius:3, boxShadow:3 }}>
          <Alert severity="error" sx={{ mb:3, textAlign:'left' }}>
            <Typography variant="subtitle1" fontWeight={600}>Authentication Failed</Typography>
            <Typography variant="body2" sx={{ mt:0.5 }}>{error}</Typography>
          </Alert>
          <Box sx={{ display:'flex', gap:2, justifyContent:'center', flexWrap:'wrap' }}>
            <Button variant="contained" onClick={() => navigate('/login')} sx={{ minWidth:120 }}>
              Back to Login
            </Button>
            <Button variant="outlined" sx={{ minWidth:120 }}
              onClick={() => { hasRun.current = false; window.location.reload(); }}>
              Try Again
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default AuthCallback;